// A lattice view with its toolbar: view direction, projection, labels / cell / axes, atom size, reset and PNG.
// The three.js part (LatticeCanvas) is loaded on first use, so the rest of the app does not carry it.
import { ActionIcon, Button, Checkbox, Group, Loader, SegmentedControl, Select, Slider, Stack, Text, Tooltip } from "@mantine/core";
import { lazy, Suspense, useRef, useState, type ReactNode } from "react";
import type { LatticeCanvasProps, ViewDir } from "./LatticeCanvas";

const LatticeCanvas = lazy(() => import("./LatticeCanvas"));

const VIEWS: { value: ViewDir; label: string }[] = [
  { value: "free", label: "Perspective view" }, { value: "a", label: "Along a" }, { value: "b", label: "Along b" },
  { value: "c", label: "Along c" }, { value: "x", label: "Along x" }, { value: "y", label: "Along y" }, { value: "z", label: "Along z" },
];

export interface LegendItem { key: string; label: string; color: string; off?: boolean; onClick?: () => void; ring?: boolean }

export function Legend({ items, title }: { items: LegendItem[]; title?: string }) {
  return (
    <Group gap={6} wrap="wrap" data-testid="lattice-legend">
      {title && <Text size="xs" c="dimmed">{title}</Text>}
      {items.map((it) => {
        const chip = (
          <Group key={it.key} gap={4} wrap="nowrap" onClick={it.onClick} style={{ cursor: it.onClick ? "pointer" : undefined, opacity: it.off ? 0.35 : 1,
            border: "1px solid var(--mantine-color-default-border)", borderRadius: 12, padding: "1px 8px 1px 4px" }}>
            <span style={{ width: 12, height: 12, borderRadius: it.ring ? 2 : 6, background: it.color, display: "inline-block",
              border: "1px solid rgba(0,0,0,0.25)" }} />
            <Text size="xs">{it.label}</Text>
          </Group>
        );
        return it.onClick ? <Tooltip key={it.key} label={it.off ? "Show" : "Hide"} withArrow>{chip}</Tooltip> : chip;
      })}
    </Group>
  );
}

export function LatticeBox({ pngName, controls, legend, footer, defaultView = "free", ...canvas }:
  Omit<LatticeCanvasProps, "view" | "ortho" | "resetKey" | "showLabels" | "showCell" | "showAxes" | "atomScale" | "canvasRef"> & {
    pngName: string; controls?: ReactNode; legend?: ReactNode; footer?: ReactNode; defaultView?: ViewDir;
  }) {
  const [view, setView] = useState<ViewDir>(defaultView);
  const [ortho, setOrtho] = useState(defaultView !== "free");
  const [reset, setReset] = useState(0);
  const [labels, setLabels] = useState(true);
  const [cell, setCell] = useState(true);
  const [axes, setAxes] = useState(true);
  const [size, setSize] = useState(0.35);
  const el = useRef<HTMLCanvasElement | null>(null);
  const save = () => {
    const c = el.current;
    if (!c) return;
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `${pngName}.png`;
    a.click();
  };
  return (
    <Stack gap={6}>
      <Group gap="xs" align="flex-end" wrap="wrap">
        {controls}
        <Select size="xs" w={150} label="View" data={VIEWS} value={view} allowDeselect={false}
          onChange={(v) => { if (!v) return; setView(v as ViewDir); setOrtho(v !== "free"); setReset((n) => n + 1); }} />
        <SegmentedControl size="xs" value={ortho ? "o" : "p"} onChange={(v) => setOrtho(v === "o")}
          data={[{ value: "p", label: "Perspective" }, { value: "o", label: "Flat" }]} />
        <Tooltip label="Reset the view"><ActionIcon variant="default" size="md" aria-label="Reset view" onClick={() => setReset((n) => n + 1)}>⟲</ActionIcon></Tooltip>
        <Button size="xs" variant="default" onClick={save}>Save PNG</Button>
      </Group>
      <Group gap="md" wrap="wrap">
        <Checkbox size="xs" label="Unit cell" checked={cell} onChange={(e) => setCell(e.currentTarget.checked)} />
        <Checkbox size="xs" label="Axes" checked={axes} onChange={(e) => setAxes(e.currentTarget.checked)} />
        <Checkbox size="xs" label="Labels" checked={labels} onChange={(e) => setLabels(e.currentTarget.checked)} />
        <Group gap={6} wrap="nowrap"><Text size="xs">Atom size</Text>
          <Slider size="xs" w={110} min={0.12} max={0.8} step={0.01} value={size} onChange={setSize} label={null} aria-label="Atom size" /></Group>
      </Group>
      {legend}
      <Suspense fallback={<Group h={canvas.height ?? 440} justify="center"><Loader size="sm" /></Group>}>
        <div data-testid="lattice-canvas">
          <LatticeCanvas {...canvas} view={view} ortho={ortho} resetKey={reset} showLabels={labels} showCell={cell} showAxes={axes}
            atomScale={size} canvasRef={(c) => { el.current = c; }} />
        </div>
      </Suspense>
      {footer}
    </Stack>
  );
}
